import React, { useState, useContext, useEffect, useRef } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { FaComments } from "react-icons/fa";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import "../styles/AISolarAssistant.css";

export default function AISolarAssistant() {
  const { user, token, updateUser } = useContext(AuthContext);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Mavic - your Solar Assistant. How can I help you today?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [credits, setCredits] = useState(user?.credits || 5);
  const [showPayPrompt, setShowPayPrompt] = useState(false);

  const chatEndRef = useRef(null);

  const baseUrl = process.env.REACT_APP_API_BASE_URL || "https://globisun-app.onrender.com";

  // keep credits synced with user context
  useEffect(() => {
    setCredits(user?.credits || 5);
  }, [user]);

  // scroll to latest message
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Handle Paystack callback automatically
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const reference = params.get("reference");
    if (!reference) return;

    const verifyPayment = async () => {
      try {
        const res = await axios.get(
          `${baseUrl}/api/paystack/verify-payment?reference=${reference}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (res.data?.success) {
          const newCredits = res.data.credits;
          setCredits(newCredits);
          updateUser({ ...user, credits: newCredits });
          setShowPayPrompt(false);
          setMessages(prev => [
            ...prev,
            { role: "assistant", content: `✅ Payment successful! You now have ${newCredits} credits.` }
          ]);

          // clean URL to remove ?reference=xxx
          window.history.replaceState({}, document.title, window.location.pathname);
        } else {
          setMessages(prev => [
            ...prev,
            { role: "assistant", content: "❌ Payment verification failed. Please try again." }
          ]);
        }
      } catch (err) {
        console.error("Payment verification error:", err);
        setMessages(prev => [
          ...prev,
          { role: "assistant", content: "❌ Payment verification error occurred." }
        ]);
      }
    };

    verifyPayment();
  }, [baseUrl, token, updateUser, user]);

  const sanitizeAssistantReply = (fullReply, history, lastUserContent) => {
    let text = (fullReply ?? "").toString().trim();
    const lastAssistant =
      [...history].reverse().find(m => m.role === "assistant")?.content?.toString() ?? "";

    if (lastAssistant && text.startsWith(lastAssistant.trim())) {
      text = text.slice(lastAssistant.trim().length).trim();
    }

    if (lastUserContent) {
      const idx = text.lastIndexOf(lastUserContent);
      if (idx !== -1) text = text.slice(idx + lastUserContent.length).trim();
    }

    text = text.replace(/^(assistant|bot)\s*:\s*/i, "").trim();
    return text.length ? text : fullReply ?? "";
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    if (credits <= 0) {
      setShowPayPrompt(true);
      return;
    }

    const userMessage = { role: "user", content: input.trim() };
    const updatedHistory = [...messages, userMessage];

    setMessages(updatedHistory);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post(
        `${baseUrl}/api/ai-chat`,
        { messages: updatedHistory },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { reply, creditsRemaining } = res.data || {};
      const cleanReply = sanitizeAssistantReply(reply, updatedHistory, userMessage.content);

      setMessages(prev => [...prev, { role: "assistant", content: cleanReply }]);
      if (typeof creditsRemaining === "number") {
        setCredits(creditsRemaining);
        updateUser({ ...user, credits: creditsRemaining });
        if (creditsRemaining <= 0) setShowPayPrompt(true);
      }
    } catch (err) {
      if (err?.response?.status === 403) {
        setShowPayPrompt(true);
        setMessages(prev => [
          ...prev,
          { role: "assistant", content: "You have no remaining credits. Please buy more to continue." }
        ]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "Oops, something went wrong." }]);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBuyCredits = async (amountCredits) => {
    try {
      const res = await axios.post(
        `${baseUrl}/api/paystack/initiate-payment`,
        { amountCredits },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = res.data.authorization_url; // Paystack redirect
    } catch (err) {
      alert("Payment initialization failed. Try again.");
    }
  };

  return (
    <div className="assistant-container">
      {/* Header */}
      <div className="assistant-header">
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <FaComments className="assistant-icon" />
        </motion.div>
        <h3 className="assistant-title">Solar Assistant</h3>
      </div>

      {/* Chat Messages */}
      <div className="assistant-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`assistant-message-row ${msg.role === "user" ? "user" : "assistant"}`}>
            <span className={`assistant-message ${msg.role}`}>
              <ReactMarkdown>{msg.content}</ReactMarkdown>
            </span>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>

      {/* Pay Prompt */}
      {showPayPrompt && (
        <div className="assistant-payprompt">
          You’ve exhausted your credits. Buy more to continue.
          <div className="assistant-paybuttons">
            <button onClick={() => handleBuyCredits(10)}>Buy 10 credits (NGN 1000)</button>
            <button onClick={() => handleBuyCredits(20)}>Buy 20 credits (NGN 2000)</button>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="assistant-input-area">
        <input
          type="text"
          placeholder="Type a message..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") sendMessage(); }}
          disabled={loading || credits <= 0}
          className="assistant-input"
        />
        <button
          onClick={sendMessage}
          disabled={loading || credits <= 0}
          className="assistant-send"
          title={loading ? "Thinking..." : "Send"}
        >
          {loading ? "..." : "➤"}
        </button>
      </div>
    </div>
  );
}
